import React, { useEffect, useMemo, useRef, useState } from 'react'

import { DateValue } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import DatePicker from './DatePicker'
import styles from './DatePickerPeriod.module.less'
import { useFormatErrorMessage } from './utils/useFormatErrorMessage'
import { validateRange } from './utils/validation'

type DatePickerProps = React.ComponentPropsWithoutRef<typeof DatePicker>
type AcceptedDateValue = DatePickerProps['value']

export type SelectedDateValue = DateValue | null | undefined

type DatePickerPeriodProps = {
  startDate: AcceptedDateValue
  endDate: AcceptedDateValue
  minDate?: DatePickerProps['minValue']
  maxDate?: DatePickerProps['minValue']
  isDisabled?: boolean
  startLabel?: string
  endLabel?: string
  onSelect?: (startTime: SelectedDateValue, endTime: SelectedDateValue) => void
  onError?: (error: boolean) => void
  granularity?: DatePickerProps['granularity']
  allowEmptyEndDate?: boolean
  startDateTransform?: (date: DateValue | null) => DateValue | null //Applies any special rules to date object which is applied onChange
  endDateTransform?: (date: DateValue | null) => DateValue | null //Applies any special rules to date object which is applied onChange
  startName?: string
  endName?: string
}

export default function DatePickerPeriod(props: DatePickerPeriodProps): React.ReactElement {
  const {
    startDate,
    endDate,
    minDate,
    maxDate,
    startLabel,
    endLabel,
    onSelect,
    onError,
    allowEmptyEndDate = false,
    startName = 'start_date',
    endName = 'end_date',
    startDateTransform,
    endDateTransform,
    ...restProps
  } = props
  const { t } = useTranslation()
  const { format } = useFormatErrorMessage()

  const [localStartDate, setLocalStartDate] = useState<DateValue | null | undefined>(startDate)
  const [localEndDate, setLocalEndDate] = useState<DateValue | null | undefined>(endDate)

  const latestStartDateRef = useRef<DateValue | null | undefined>(startDate)
  const latestEndDateRef = useRef<DateValue | null | undefined>(endDate)

  // We only want to update local start/end date when the prop changes, not on initial render.
  // This is to avoid double-rendering when the parent component sets the initial date.

  const [initialStartDate, initialEndDate] = useMemo(() => [startDate, endDate], [])

  useEffect(() => {
    if (!initialStartDate || startDate?.compare(initialStartDate) !== 0) {
      latestStartDateRef.current = startDate
      setLocalStartDate(startDate)
    }
    if (!initialEndDate || endDate?.compare(initialEndDate) !== 0) {
      latestEndDateRef.current = endDate
      setLocalEndDate(endDate)
    }
  }, [endDate, startDate, initialStartDate, initialEndDate])

  const validationResult = validateRange({
    startDate: localStartDate,
    endDate: localEndDate,
    minValue: minDate,
    maxValue: maxDate,
    allowEmptyEndDate,
  })

  const startDateIsInvalid =
    !validationResult.isValid &&
    (validationResult.error === 'MISSING_START_DATE' ||
      validationResult.error === 'RANGE_UNDERFLOW' ||
      validationResult.error === 'REVERSED_RANGE')
  const endDateIsInvalid =
    !validationResult.isValid &&
    (validationResult.error === 'MISSING_END_DATE' ||
      validationResult.error === 'RANGE_OVERFLOW' ||
      validationResult.error === 'REVERSED_RANGE')

  useEffect(() => {
    onError?.(!validationResult.isValid)
  }, [onError, validationResult.isValid])

  function handleOnEndDateChange(date: DateValue | null): void {
    const newDate = endDateTransform && date ? endDateTransform(date) : date
    latestEndDateRef.current = newDate
    setLocalEndDate(newDate)
  }

  function handleOnStartDateChange(date: DateValue | null): void {
    const newDate = startDateTransform && date ? startDateTransform(date) : date
    latestStartDateRef.current = newDate
    setLocalStartDate(newDate)
  }

  function handleOnBlur(): void {
    if (validationResult.isValid) {
      onSelect?.(localStartDate, localEndDate)
    }
  }

  function handleOpenChange(isOpen: boolean): void {
    if (!isOpen) {
      const validation = validateRange({
        startDate: latestStartDateRef.current,
        endDate: latestEndDateRef.current,
        minValue: minDate,
        maxValue: maxDate,
        allowEmptyEndDate,
      })
      if (validation.isValid) {
        onSelect?.(latestStartDateRef.current, latestEndDateRef.current)
      }
    }
  }

  return (
    <div className={styles.DatePickerPeriod}>
      <DatePicker
        data-testid="start-date-picker"
        isPeriod
        isRequired
        name={startName}
        minValue={minDate}
        maxValue={maxDate}
        value={localStartDate}
        label={startLabel ?? t('Start date')}
        onChange={handleOnStartDateChange}
        onBlur={handleOnBlur}
        onOpenChange={handleOpenChange}
        isInvalid={startDateIsInvalid || undefined}
        errorMessage={startDateIsInvalid ? format(validationResult.error) : undefined}
        showWeekNumbers
        {...restProps}
      />
      <DatePicker
        data-testid="end-date-picker"
        isPeriod
        isRequired={!allowEmptyEndDate}
        value={localEndDate}
        label={endLabel ?? t('End date')}
        onChange={handleOnEndDateChange}
        onBlur={handleOnBlur}
        onOpenChange={handleOpenChange}
        isInvalid={endDateIsInvalid || undefined}
        errorMessage={endDateIsInvalid ? format(validationResult.error) : undefined}
        minValue={minDate}
        maxValue={maxDate}
        name={endName}
        isEndPeriod
        allowEmpty={allowEmptyEndDate}
        showWeekNumbers
        {...restProps}
      />
    </div>
  )
}
